import { useAbility } from '@casl/vue';
import { computed } from 'vue';
import { useAbilities } from '@/queries/auth/use-login';
import { useLegalEntities } from '@/queries/use-legal-entities';
import { useMe } from '@/queries/use-users';
import { useLegalEntityFilterStore } from '@/stores/legal-entity-filter.store';

/**
 * Global legal entity filter. Offers all legal entities to users who may operate on every station
 * (e.g. admins), otherwise only those of the user's assigned stations. It is only visible with
 * more than one option. A stored selection the user may not choose counts as "all legal entities".
 */
export const useLegalEntityFilter = () => {
  const store = useLegalEntityFilterStore();
  const { data: legalEntities } = useLegalEntities();
  const { data: user } = useMe();
  const ability = useAbility();
  const { data: abilityRules } = useAbilities();

  // Users restricted to their assigned stations get an `operateOn` rule with conditions
  const canOperateOnAllStations = computed(
    () =>
      !!abilityRules.value &&
      ability.rulesFor('operateOn', 'Station').some((rule) => !rule.inverted && !rule.conditions),
  );

  const legalEntityOptions = computed(() => {
    const assignedLegalEntityIds = new Set(
      (user.value?.stations ?? []).map((station) => station.legalEntity?.id).filter(Boolean),
    );
    return (legalEntities.value ?? [])
      .filter((legalEntity) => legalEntity.isActive)
      .filter(
        (legalEntity) =>
          canOperateOnAllStations.value || assignedLegalEntityIds.has(legalEntity.id),
      )
      .map((legalEntity) => ({ label: legalEntity.name, value: legalEntity.id }));
  });

  const isLegalEntityFilterVisible = computed(() => legalEntityOptions.value.length > 1);

  const legalEntityId = computed<string | null>({
    get: () =>
      isLegalEntityFilterVisible.value &&
      legalEntityOptions.value.some((option) => option.value === store.legalEntityId)
        ? store.legalEntityId
        : null,
    set: (value) => store.setLegalEntityId(value),
  });

  return {
    legalEntityId,
    legalEntityOptions,
    isLegalEntityFilterVisible,
    legalEntities,
  };
};
